import React, { useEffect, useRef, useState } from "react";

type ProductRow = { [k: string]: any };

type Props = {
  show: boolean;
  onClose: () => void;
  onSubmit: () => void;
  formData?: any;
  productRows?: ProductRow[];
  currencyName?: string;
  headerLogoUrl?: string;
  footerImageUrl?: string;
  quotationFormat?: string;
};

export default function OrderPreview({
  show,
  onClose,
  onSubmit,
  formData,
  productRows = [],
  currencyName = "INR",
  headerLogoUrl,
  footerImageUrl,
  quotationFormat,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [downloading, setDownloading] = useState(false);

  const handleSubmit = async () => {
    try {
      setDownloading(true);
      await onSubmit();
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    if (show && containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [show]);

  if (!show) return null;

  const totals = productRows.reduce(
    (a, r) => {
      const net = Number(r?.net_price || 0);
      const igstPct = Number(r?.igst || 0);
      const halfPct = igstPct / 2;

      const igstAmt = (net * igstPct) / 100;
      const halfAmt = (net * halfPct) / 100;

      a.net += net;
      a.igst += igstAmt;
      a.sgst += halfAmt;
      a.cgst += halfAmt;

      return a;
    },
    {
      net: 0,
      igst: 0,
      sgst: 0,
      cgst: 0,
    }
  );

  const state = formData?.state ?? formData?.company_details?.state?.name;
  const quotationType = formData?.quotation_type;

  const isMH = state === "Maharashtra";

  const taxTotal = isMH ? totals.sgst + totals.cgst : totals.igst;

  const grandTotal = totals.net + taxTotal;
  const hasText = (v?: string) =>
    !!v && v?.replace(/<[^>]*>|&nbsp;/g, "").trim().length > 0;
  const getColSpan = () => {
    if (quotationType === "GW") return 15;
    if (state === "Maharashtra") return 13;
    return 11;
  };
  return (
    <div style={overlay}>
      <div style={modal}>
        <div style={toolbar}>
          <h4>ORDER PREVIEW</h4>
        </div>
        <div style={scrollWrap}>
          <div ref={containerRef} style={paper}>
            <style>{printCss}</style>

            {/* HEADER */}
            <table className="hdr">
              <tbody>
                <tr>
                  <td
                    style={{
                      width: "20%",
                      borderBottom: "none",
                      textAlign: "center",
                      border: "none",
                    }}
                  >
                    <div
                      className="logo"
                      style={{
                        display: "inline-block",
                        position: "relative",
                        textAlign: "center",
                        maxWidth: "80%",
                        marginTop: "-8px",
                      }}
                    >
                      {headerLogoUrl ? (
                        <img
                          src={headerLogoUrl}
                          alt="logo"
                          style={{ display: "block", margin: "0 auto" }}
                        />
                      ) : (
                        <div className="logo-fallback">QUOTATION</div>
                      )}

                      <p
                        style={{
                          marginTop: "5px",
                          fontSize: "12px",
                          fontWeight: 700,
                          color: "#333",
                          textAlign: "center",
                        }}
                      >
                        ISO 9001-2015
                      </p>
                    </div>
                  </td>

                  <td
                    className="d-flex meta-cell"
                    style={{
                      border: "none",
                      lineHeight: "2",
                    }}
                  >
                    <div className="meta">
                      {/* ADDRESS + CALL */}
                      <b className="meta-label">Address :</b>
                      <b>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;217,
                        2nd Floor, Champaklal Industrial Estate, Sion East,
                        Mumbai – 400022,
                        India&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                      </b>
                      <b className="meta-label">Call :</b>
                      <b>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;91-022-43159100
                      </b>
                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                      <br />
                      {/* EMAIL */}
                      <b className="meta-label">Email :</b>
                      <b>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;sales@chromatographyworld.com,&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        speed@chromatographyworld.com,&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        gm-support@chromatographyworld.com&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                      </b>
                      <br />
                      {/* GST + UDYAM */}
                      <b style={{ color: "#b30f16" }}>GSTN :</b>
                      <b>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;27AAGFC1217K1ZM&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                      </b>
                      <b
                        className="meta-label alert"
                        style={{ color: "#b30f16" }}
                      >
                        UDYAM/MSME No. :
                      </b>
                      <b>
                        UDYAM-MH-19-0078510&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                      </b>
                      {/* WEB */}
                      <b className="meta-label">Web :</b>
                      <a className="meta-link" style={{ color: "blue" }}>
                        <b>
                          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;www.chromatographyworld.com
                        </b>
                      </a>
                      {/* BANK DETAILS */}
                      <br />
                      <b className="meta-label">Bank :</b>
                      <b>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Kotak
                        Mahindra
                        Bank&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                      </b>
                      <b className="meta-label">Branch :</b>
                      <b>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Matunga&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                      </b>
                      <b className="meta-label">IFSC :</b>
                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                      <b>
                        &nbsp;KKBK0000644&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                      </b>
                      <b className="meta-label">A/C :</b>
                      <b>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;4611234274
                      </b>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            <table>
              <tr>
                <td
                  style={{
                    textAlign: "center",
                    padding: "5px 8px",
                    fontSize: "13px",
                    backgroundColor: "#2b2b2b",
                    color: "#f5f2e8",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                    fontWeight: "700",
                  }}
                >
                  <b>Authorised For :</b> Qualisil, Qaliseal, Gas World,
                  Macherey Nagel, G.L.Science (GC Columns), S.A.S.Corporation,
                  Nomura Chemicals (Develosil), Sielc (Primesep), Sciencix, Poly
                  LC, MZAnalysentechnik, Sepax, Frontier Lab. Click&nbsp;
                  <b style={{ color: "#edd38c", textDecoration: "underline" }}>
                    www.chromatographyworld.com
                  </b>{" "}
                  for more details.
                </td>
              </tr>
            </table>

            <h6
              style={{
                margin: "5px",
                color: "#800000",
                textAlign: "center",
                fontSize: "18px",
              }}
            >
              Purchase Order Acknowledgement
            </h6>

            {/* SHIPPING */}
            <table className="ship">
              <tbody>
                {/* ================= TOP INFO ================= */}
                <tr>
                  <th>Customer Order</th>
                  <td colSpan={2}>{formData?.customer_order_no}</td>

                  <th>Order Date</th>
                  <td>{formData?.date}</td>

                  <th>Quotation No.</th>
                  <td colSpan={2}>{formData?.unique_quotation_no}</td>

                  <th>Quotation Date</th>
                  <td colSpan={2}>{formData?.date}</td>
                </tr>

                <tr>
                  <th>Order Ref</th>
                  <td colSpan={2}>{formData?.enq_ref}</td>

                  <th>Date</th>
                  <td>{formData?.date}</td>

                  <th>Credit Terms</th>
                  <td colSpan={2}>{formData?.delivery_date_custom ?? null}</td>

                  <th>Prefered Courier</th>
                  <td colSpan={2}>
                    {formData?.country ?? formData?.courier_name}
                  </td>
                </tr>

                {/* ================= BILLING (NOW OLD SHIPPING STYLE) ================= */}
                <tr
                  style={{
                    backgroundColor: "#CCECFF",
                    border: "0.4px solid #9AA3A8",
                    color: "rgb(179,15,22)",
                  }}
                >
                  <th
                    style={{
                      border: "0.4px solid #9AA3A8",
                      color: "rgb(179,15,22)",
                    }}
                  >
                    Billing Name
                  </th>
                  <td
                    colSpan={6}
                    style={{
                      border: "0.4px solid #9AA3A8",
                      color: "rgb(179,15,22)",
                    }}
                  >
                    {formData?.company_name ??
                      formData?.company_details?.company_name}
                  </td>

                  <th
                    style={{
                      border: "0.4px solid #9AA3A8",
                      color: "rgb(179,15,22)",
                    }}
                  >
                    GSTN
                  </th>
                  <td
                    colSpan={3}
                    style={{
                      border: "0.4px solid #9AA3A8",
                      color: "rgb(179,15,22)",
                    }}
                  >
                    {formData?.gst_number ??
                      formData?.company_details?.gst_number}
                  </td>
                </tr>

                <tr
                  style={{
                    backgroundColor: "#CCECFF",
                    border: "0.4px solid #9AA3A8",
                    color: "rgb(179,15,22)",
                  }}
                >
                  <th
                    style={{
                      border: "0.4px solid #9AA3A8",
                      color: "rgb(179,15,22)",
                    }}
                  >
                    Billing Address
                  </th>
                  <td
                    colSpan={2}
                    style={{
                      border: "0.4px solid #9AA3A8",
                      color: "rgb(179,15,22)",
                    }}
                  >
                    {formData?.billing_address}
                  </td>

                  <th
                    style={{
                      border: "0.4px solid #9AA3A8",
                      color: "rgb(179,15,22)",
                    }}
                  >
                    Pin Code
                  </th>
                  <td
                    style={{
                      border: "0.4px solid #9AA3A8",
                      color: "rgb(179,15,22)",
                    }}
                  >
                    {formData?.billing_pin_code}
                  </td>

                  <th
                    style={{
                      border: "0.4px solid #9AA3A8",
                      color: "rgb(179,15,22)",
                    }}
                  >
                    City
                  </th>
                  <td
                    style={{
                      border: "0.4px solid #9AA3A8",
                      color: "rgb(179,15,22)",
                    }}
                  >
                    {formData?.billing_city}
                  </td>

                  <th
                    style={{
                      border: "0.4px solid #9AA3A8",
                      color: "rgb(179,15,22)",
                    }}
                  >
                    State
                  </th>
                  <td
                    style={{
                      border: "0.4px solid #9AA3A8",
                      color: "rgb(179,15,22)",
                    }}
                  >
                    {state}
                  </td>

                  <th
                    style={{
                      border: "0.4px solid #9AA3A8",
                      color: "rgb(179,15,22)",
                    }}
                  >
                    Country
                  </th>
                  <td
                    style={{
                      border: "0.4px solid #9AA3A8",
                      color: "rgb(179,15,22)",
                    }}
                  >
                    {formData?.country ??
                      formData?.company_details?.country?.name}
                  </td>
                </tr>

                <tr
                  style={{
                    backgroundColor: "#CCECFF",
                    border: "0.4px solid #9AA3A8",
                    color: "rgb(179,15,22)",
                  }}
                ></tr>

                <tr
                  style={{
                    backgroundColor: "#CCECFF",
                    border: "0.4px solid #9AA3A8",
                    color: "rgb(179,15,22)",
                  }}
                >
                  <th
                    style={{
                      border: "0.4px solid #9AA3A8",
                      color: "rgb(179,15,22)",
                    }}
                  >
                    Contact Person
                  </th>
                  <td
                    colSpan={3}
                    style={{
                      border: "0.4px solid #9AA3A8",
                      color: "rgb(179,15,22)",
                    }}
                  >
                    {formData?.contact_person}
                  </td>

                  <th
                    style={{
                      border: "0.4px solid #9AA3A8",
                      color: "rgb(179,15,22)",
                    }}
                  >
                    Email
                  </th>
                  <td
                    colSpan={2}
                    style={{
                      border: "0.4px solid #9AA3A8",
                      color: "rgb(179,15,22)",
                    }}
                  >
                    {formData?.billing_email}
                  </td>

                  <th
                    style={{
                      border: "0.4px solid #9AA3A8",
                      color: "rgb(179,15,22)",
                    }}
                  >
                    Phone
                  </th>
                  <td
                    style={{
                      border: "0.4px solid #9AA3A8",
                      color: "rgb(179,15,22)",
                    }}
                  >
                    {formData?.billing_landline}
                  </td>

                  <th
                    style={{
                      border: "0.4px solid #9AA3A8",
                      color: "rgb(179,15,22)",
                    }}
                  >
                    Mobile
                  </th>
                  <td
                    style={{
                      border: "0.4px solid #9AA3A8",
                      color: "rgb(179,15,22)",
                    }}
                  >
                    {formData?.billing_mobile}
                  </td>
                </tr>

                {/* ================= SHIPPING (NOW OLD BILLING STYLE) ================= */}
                <tr
                  style={{
                    backgroundColor: "#99CCFF",
                    border: "0.4px solid #C2C9CC",
                    color: "#111",
                  }}
                >
                  <th style={{ border: "0.4px solid #9AA3A8", color: "#111" }}>
                    Shipping Name
                  </th>
                  <td
                    colSpan={6}
                    style={{ border: "0.4px solid #9AA3A8", color: "#111" }}
                  >
                    {formData?.company_name ??
                      formData?.company_details?.company_name}
                  </td>

                  <th style={{ border: "0.4px solid #9AA3A8", color: "#111" }}>
                    GSTN
                  </th>
                  <td
                    colSpan={3}
                    style={{ border: "0.4px solid #9AA3A8", color: "#111" }}
                  >
                    {formData?.gst_number ??
                      formData?.company_details?.gst_number}
                  </td>
                </tr>

                <tr
                  style={{
                    backgroundColor: "#99CCFF",
                    border: "0.4px solid #9AA3A8",
                    color: "#111",
                  }}
                >
                  <th style={{ border: "0.4px solid #9AA3A8", color: "#111" }}>
                    Shipping Address
                  </th>
                  <td
                    colSpan={2}
                    style={{ border: "0.4px solid #9AA3A8", color: "#111" }}
                  >
                    {formData?.shipping_address}
                  </td>

                  <th style={{ border: "0.4px solid #9AA3A8", color: "#111" }}>
                    Pin Code
                  </th>
                  <td style={{ border: "0.4px solid #9AA3A8", color: "#111" }}>
                    {formData?.shipping_pin_code}
                  </td>
                  <th style={{ border: "0.4px solid #9AA3A8", color: "#111" }}>
                    City
                  </th>
                  <td style={{ border: "0.4px solid #9AA3A8", color: "#111" }}>
                    {formData?.shipping_city}
                  </td>

                  <th style={{ border: "0.4px solid #9AA3A8", color: "#111" }}>
                    State
                  </th>
                  <td style={{ border: "0.4px solid #9AA3A8", color: "#111" }}>
                    {formData?.shipping_state_name}
                  </td>

                  <th style={{ border: "0.4px solid #9AA3A8", color: "#111" }}>
                    Country
                  </th>
                  <td style={{ border: "0.4px solid #9AA3A8", color: "#111" }}>
                    {formData?.country ??
                      formData?.company_details?.country?.name}
                  </td>
                </tr>

                <tr
                  style={{
                    backgroundColor: "#99CCFF",
                    border: "0.4px solid #9AA3A8",
                    color: "#111",
                  }}
                ></tr>

                <tr
                  style={{
                    backgroundColor: "#99CCFF",
                    border: "0.4px solid #9AA3A8",
                    color: "#111",
                  }}
                >
                  <th style={{ border: "0.4px solid #9AA3A8", color: "#111" }}>
                    Contact Person
                  </th>
                  <td
                    colSpan={3}
                    style={{ border: "0.4px solid #9AA3A8", color: "#111" }}
                  >
                    {formData?.contact_person}
                  </td>

                  <th
                    colSpan={2}
                    style={{ border: "0.4px solid #9AA3A8", color: "#111" }}
                  >
                    Email
                  </th>
                  <td style={{ border: "0.4px solid #9AA3A8", color: "#111" }}>
                    {formData?.shipping_email}
                  </td>

                  <th style={{ border: "0.4px solid #9AA3A8", color: "#111" }}>
                    Phone
                  </th>
                  <td style={{ border: "0.4px solid #9AA3A8", color: "#111" }}>
                    {formData?.shipping_landline}
                  </td>

                  <th style={{ border: "0.4px solid #9AA3A8", color: "#111" }}>
                    Mobile
                  </th>
                  <td style={{ border: "0.4px solid #9AA3A8", color: "#111" }}>
                    {formData?.shipping_mobile}
                  </td>
                </tr>
              </tbody>  
            </table>

            {/* ITEMS */}
            <table className="items">
              <thead>
                <tr>
                  <th className="money">Sr</th>
                  <th className="money">Part No.</th>
                  <th className="money">Description</th>

                  {quotationType == "GW" && (
                    <>
                      <th className="money">Maker</th>
                      <th className="money">UOM</th>
                    </>
                  )}
                  <th className="money">HSN</th>
                  <th className="money">Qty</th>
                  <th className="money-real">Unit Price ({currencyName})</th>
                  <th className="money-real">Disc %</th>
                  <th className="money-real">Net Price ({currencyName})</th>

                  {isMH ? (
                    <>
                      <th className="money-real">SGST %</th>
                      <th className="money-real">SGST AMT ({currencyName})</th>
                      <th className="money-real">CGST %</th>
                      <th className="money-real">CGST AMT ({currencyName})</th>
                    </>
                  ) : (
                    <>
                      <th className="money-real">IGST %</th>
                      <th className="money-real">IGST AMT ({currencyName})</th>
                    </>
                  )}

                  <th className="money-real">Total AMT ({currencyName})</th>
                  <th>Delivery</th>
                </tr>
              </thead>

              <tbody>
                {productRows.map((it, i) => {
                  const net = Number(it?.net_price || 0);
                  const igst = Number(it?.igst || 0);
                  const half = igst / 2;

                  const igstAmt = (net * igst) / 100;
                  const halfAmt = (net * half) / 100;

                  const rowTotal = isMH
                    ? net + halfAmt + halfAmt
                    : net + igstAmt;

                  const showDetails =
                    quotationType === "GW"
                      ? hasText(it.specification)
                      : hasText(it.product_specification);
                  return (
                    <React.Fragment key={i}>
                        <tr
                        className={`item-row ${i % 2 === 0 ? "odd" : "even"}`}
                        style={{
                          backgroundColor:
                              quotationType === "GW"
                                ? i % 2 === 0
                                  ? "#C1D9BF"
                                  : "#D9E9D6"
                                : i % 2 === 0
                                ? "#faeff0"
                                : "#f1d3d6",
                          }}
                      >
                        <td>{i + 1}</td>
                        <td>{it.part_no}</td>
                        <td>
                          {
                            <span
                              dangerouslySetInnerHTML={{
                                __html: it.description,
                              }}
                            />
                          }
                        </td>

                        {quotationType == "GW" && (
                          <>
                            <td>{it?.principal?.type ?? it?.principal}</td>
                            <td>{it?.uom?.uom ?? it?.uom}</td>
                          </>
                        )}

                        <td className="money-real">{it.hsn_code}</td>
                        <td>{it.quantity}</td>
                        <td className="money-real">{it.price}</td>
                        <td className="money-real">{it.discount}</td>
                        <td className="money-real">{net.toFixed(2)}</td>

                        {isMH ? (
                          <>
                            <td className="money-real">{half.toFixed(2)}</td>
                            <td className="money-real">{halfAmt.toFixed(2)}</td>
                            <td className="money-real">{half.toFixed(2)}</td>
                            <td className="money-real">{halfAmt.toFixed(2)}</td>
                          </>
                        ) : (
                          <>
                            <td className="money-real">{igst.toFixed(2)}</td>
                            <td className="money-real">{igstAmt.toFixed(2)}</td>
                          </>
                        )}

                        <td className="money-real">{rowTotal.toFixed(2)}</td>
                        <td>
                          {
                            <span
                              dangerouslySetInnerHTML={{
                                __html: it.notes,
                              }}
                            />
                          }
                        </td>
                      </tr>

                      {/* ================= ALL DETAILS IN ONE TD ================= */}
                      {showDetails && (
                        <tr
                          className={`item-row ${i % 2 === 0 ? "odd" : "even"}`}
                          style={{
                            backgroundColor:
                              i % 2 === 0 ? "#faeff0" : "#f1d3d6",
                          }}
                        >
                          <td
                            style={{
                              backgroundColor:
                                quotationType === "GW" ? "#f1d3d6" : undefined,
                            }}
                          ></td>
                          <td
                            colSpan={getColSpan()}
                            style={{
                              backgroundColor:
                                quotationType === "GW" ? "#f1d3d6" : undefined,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              {hasText(it.product_specification) && (
                                <div className="detail-text  html-content">
                                  <b className="label-heading">Comments :</b>{" "}
                                  <span
                                    dangerouslySetInnerHTML={{
                                      __html: it.product_specification,
                                    }}
                                  />
                                </div>
                              )}

                              {quotationType === "GW" &&
                                hasText(it.specification) && (
                                  <div className="detail-text html-content">
                                    <b className="label-heading">
                                      Specification with Heading :
                                    </b>{" "}
                                    <span
                                      dangerouslySetInnerHTML={{
                                        __html: it.specification,
                                      }}
                                    />
                                  </div>
                                )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}

                {formData?.product_description && (
                  <tr style={{ backgroundColor: "#bcecfa" }}>
                    <td
                      colSpan={
                        state === "Maharashtra" && quotationType == "GW"
                          ? 16
                          : state !== "Maharashtra" && quotationType == "GW"
                          ? 16
                          : state === "Maharashtra"
                          ? 14
                          : 13
                      }
                      style={{
                        border: "none",
                        padding: "6px 4px",
                        wordBreak: "break-word",
                      }}
                    >
                      <b>NOTES :</b>{" "}
                      <span
                        dangerouslySetInnerHTML={{
                          __html: formData.product_description,
                        }}
                      />
                    </td>
                  </tr>
                )}
                {state === "Maharashtra" && (
                  <tr className="total-row">
                    <td
                      colSpan={quotationType === "GW" ? 9 : 7}
                      className="label"
                    >
                      Grand Total ({currencyName})
                    </td>

                    <td className="money-real">{totals.net.toFixed(2)}</td>
                    <td></td>
                    <td className="money-real">{totals.sgst.toFixed(2)}</td>
                    <td></td>
                    <td className="money-real">{totals.cgst.toFixed(2)}</td>

                    <td className="money-real">{grandTotal.toFixed(2)}</td>

                    <td></td>
                  </tr>
                )}

                {state !== "Maharashtra" && (
                  <tr className="total-row">
                    <td
                      colSpan={quotationType === "GW" ? 9 : 7}
                      className="label"
                    >
                      Grand Total ({currencyName})
                    </td>

                    <td className="money-real">{totals.net.toFixed(2)}</td>
                    <td></td>
                    <td className="money-real">{totals.igst.toFixed(2)}</td>

                    <td className="money-real">{grandTotal.toFixed(2)}</td>

                    <td></td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* ================= FOOTER TOTALS ================= */}
            <table className="footer">
              <tbody>
                <tr>
                  <td
                    colSpan={3}
                    style={{
                      backgroundImage: `url(${footerImageUrl})`,
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "center",
                      backgroundSize: "100% 100%",
                      padding: 0,
                      verticalAlign: "top",
                      border: "none",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        width: "100%",
                        boxSizing: "border-box",
                        padding: "5px",
                      }}
                    >
                      {/* TERMS */}

                      <div style={{ width: "55%" }}>
                        <div
                          style={{
                            color: "#004f6e",
                            fontWeight: 700,
                            fontSize: "13px",
                            marginBottom: "5px",
                          }}
                        >
                          Terms & Conditions
                        </div>

                        <div
                          className="html-content"
                          dangerouslySetInnerHTML={{
                            __html: formData?.payment_term_condition || "",
                          }}
                        />
                      </div>

                      {/* SIGNATURE */}
                      <div
                        style={{
                          marginTop: "10px",
                          width: "25%",
                          color: "#333",
                          fontWeight: 700,
                          marginLeft: "auto",
                        }}
                      >
                        <p
                          style={{
                            color: "#004f6e",
                            margin: "0 0 2px 0",
                            fontSize: "14px",
                            textAlign: "right",
                          }}
                        >
                          For Chromatography World
                        </p>
                        <p
                          style={{
                            color: "#004f6e",
                            margin: 10,
                            fontSize: "14px",
                            textAlign: "right",
                          }}
                        >
                          Authorized Signatory
                        </p>
                        <p
                          style={{
                            color: "#333",
                            margin: 2,
                            fontSize: "12px",
                            textAlign: "right",
                          }}
                        >
                          <u>{formData?.prepard_by}</u>
                        </p>
                        <br />
                      </div>
                    </div>
                  </td>
                </tr>

                <tr>
                  <td
                    colSpan={100}
                    style={{
                      backgroundColor: "#c9f1ff",
                      color: "#0b3a4a",
                      textAlign: "center",
                      padding: "10px 12px",
                      border: "none",
                      letterSpacing: "0.2px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: 500,
                        marginBottom: "10px",
                      }}
                    >
                      {quotationFormat}
                    </div>

                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: 700,
                        color: "maroon",
                      }}
                    >
                      We Value your Association with us. Thank you!
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div style={toolbar}>
          <h5> </h5>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-md btn-soft-warning" onClick={onClose}>
              Edit
            </button>
            <button
              className="btn btn-md btn-soft-success"
              onClick={handleSubmit}
              disabled={downloading}
            >
              {downloading
                ? "Generating…"
                : formData?.unique_quotation_no
                ? "Generat Order"
                : "Generate Quotation"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- styles ---------------- */

const overlay: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,.65)",
  zIndex: 9999,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};

const modal: React.CSSProperties = {
  width: "96%",
  maxWidth: 1400,
  height: "90vh",
  background: "white",
  borderRadius: 8,
  display: "flex",
  flexDirection: "column",
};

const toolbar: React.CSSProperties = {
  padding: 10,
  borderBottom: "1px solid #eee",
  display: "flex",
  justifyContent: "center",
  textAlign: "center",
};

const scrollWrap: React.CSSProperties = {
  flex: 1,
  overflowY: "auto",
  overflowX: "hidden",
};

const paper: React.CSSProperties = {
  background: "#fff",
  padding: 8,
  margin: "-10px auto",
  width: "auto", //
};

const printCss = `


/* ================= FONT LOAD : CALIBRI ================= */

@font-face {
  font-family: "Calibri";
  src: url("/fonts/calibri.ttf") format("truetype");
  font-weight: 400;
  font-style: normal;
}   

@font-face {
  font-family: "Calibri";
  src: url("/fonts/calibrib.ttf") format("truetype");
  font-weight: 700;
  font-style: normal;
}

@font-face {
  font-family: "Calibri";
  src: url("/fonts/calibrii.ttf") format("truetype");
  font-weight: 400;
  font-style: italic;
}

@font-face {
  font-family: "Calibri";
  src: url("/fonts/calibriz.ttf") format("truetype");
  font-weight: 700;
  font-style: italic;
}

/* ================= BASE ================= */
body, table, th, td {
  font-family: "Calibri", Candara, "Segoe UI", Arial, sans-serif;
  font-size: 12px;
  line-height: 1.2;
  color: #1f1f1f;
  font-variant-numeric: tabular-nums;
}

table {
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
}

/* ================= UNIFORM BORDER SYSTEM ================= */
th, td {
  border: none;
  padding: 4px;
  vertical-align: top;
  border: 0.4px solid #C2C9CC;
}


tr {
  page-break-inside: auto;
}

/* ================= HEADER TABLE ================= */
.hdr {
  background: #bcecfa;
}

.hdr th {
  color: #006c95;
  font-weight: 700;
}

.hdr td {
  color: #333;
}

/* ================= SHIP TABLE ================= */
.ship {
  background: #f2f8f9;
  margin-bottom: 7px;
}

.ship th {
  color: #004f6e;
  font-weight: 700;
}

.ship td {
  color: #222;
}

/* ================= ITEMS ================= */
.items {
  margin-bottom: 7px;
}

.items th {
  background: #eef6f9;
  color: #004f6e;
  font-weight: 700;
}

.items td {
  color: #333;
}


/* ================= SPEC / COMMENT ================= */
.spec-row td,
.comment-row td {
  font-size: 9px;
  line-height: 1.15;
  padding-top: 2px;
  padding-bottom: 2px;
}

.spec-row td p,
.comment-row td p {
  margin: 0 !important;
  padding: 0 !important;
}

.spec-row td br,
.comment-row td br {
  display: none !important;
}

/* ================= TOTAL ROW ================= */
.items tbody tr.total-row td {
  background: #333333 !important;
  color: #f5f2e8;
  font-size: 15px;
  font-weight: 700;
  border: none !important;
}

.items tbody tr.total-row .label {
  text-align: center;
}
  .meta-label {
    color: #004f6e;
    text-transform: capitalize;
    letter-spacing: 1.5px;
    font-weight: 700;
    font-size: 12px;
}

.html-content ol li[data-list="ordered"] {
  list-style-type: decimal !important;
  list-style-position: outside !important;
  padding-left: 0px !important;  
  margin: 0;
  font-weight: 700;
  margin-left:15px;
}

.html-content ol li[data-list="bullet"] {
  list-style-type: disc !important;
  list-style-position: outside !important;
  padding-left: 0px !important;  
  margin: 0;
  font-weight: 700;
  margin-left:5px;
}
.html-content ol li {
  list-style-type: decimal !important;
  list-style-position: outside !important;
  padding-left: 0px !important;  
  margin: 0;
  font-weight: 700;
  margin-left:0px;
}


.html-content li,p {
  margin-bottom: 2px;
}

.label-heading {
  display: flex;
  flex-direction: column;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  color: #006c95;
  margin-bottom: 2px;
}

.items th.money,
.items td.money,
.items tbody tr.total-row td {
  white-space: nowrap !important;
  word-break: keep-all !important;
  overflow-wrap: normal !important;
}

.items th.money-real,
  .items td.money-real {
    white-space: nowrap;
    text-align: right;
    padding: 2px;
  }

  .total-row th.money-real,
  .items td.money-real {
    white-space: nowrap;
    text-align: right;
    padding: 2px;
  }

.detail-text {
  font-size: 12px;
  padding: 0;
  white-space: normal;
}

.ql-container,
.ql-editor {
  background: #ffffff !important;
  color: #000;
}
`;
